/**
 * Chart-series builders.
 *
 * Since payload schema v8, SIPS sends REAL per-year series (alignmentSeries,
 * taxSeries, endOfPlanAccountMix) and the `...FromReal` adapters below map
 * them to the chart shapes. Older reports lack those fields, so the original
 * synthesizers remain as fallbacks: they invent plausible curves whose
 * ENDPOINTS ARE PINNED TO THE REAL VALUES, so the shapes are illustrative
 * while every labeled number is true to the plan. Every consumer must render
 * CHART_DISCLAIMER either way (real series are still model projections).
 */

import type {
  AlignmentSeriesPoint,
  EndOfPlanAccountMix,
  EnrichedPerson,
  IncomeSeriesPoint,
  RothConversionEntry,
  TaxSeriesPoint,
} from '../types/webReport';

export const CHART_DISCLAIMER =
  "Chart values are projected from your plan's assumptions for illustration; actual amounts will vary.";

const DEFAULT_COLA = 0.025;

export interface IncomeYear {
  year: number;
  withdrawals: number;
  socialSecurity: number;
  pension: number;
  wages: number;
}

/**
 * Year-by-year income stack for the Details act: wages run until each
 * person's retirement, Social Security and pension kick in at their start
 * ages, withdrawals fill the gap to the (inflation-adjusted) target income.
 */
export function buildIncomeSeries(input: {
  targetSpend: number;
  planHorizonYears: number;
  people: EnrichedPerson[];
  colaPct?: number;
}): IncomeYear[] {
  const horizon = Math.max(1, Math.min(60, Math.round(input.planHorizonYears || 30)));
  const cola = input.colaPct ?? DEFAULT_COLA;
  const series: IncomeYear[] = [];

  for (let year = 1; year <= horizon; year++) {
    let wages = 0;
    let ss = 0;
    let pension = 0;

    for (const p of input.people) {
      const age = p.age ?? null;
      const currentAge = age ?? 65;
      const ageThisYear = currentAge + (year - 1);

      // Wages until retirement age (already-retired people have no wages).
      if (!p.retired && p.wages.amount && p.wages.amount > 0) {
        const stopsAt = p.retireAge ?? currentAge + 1;
        if (ageThisYear < stopsAt) wages += p.wages.amount;
      }

      // Social Security: from year 1 when already claiming; else from the
      // later of FRA-ish start (we only know the benefit amount — assume it
      // starts at retirement or age 67, whichever is later).
      if (p.socialSecurity.amount && p.socialSecurity.amount > 0) {
        const startAge = p.socialSecurity.claimingNow
          ? currentAge
          : Math.max(p.retireAge ?? 67, 67);
        if (ageThisYear >= startAge) {
          ss += p.socialSecurity.amount * Math.pow(1 + cola, Math.max(0, year - 1));
        }
      }

      if (p.pension && p.pension.amount > 0) {
        const startAge = p.pension.startAge ?? Math.max(p.retireAge ?? 65, 65);
        if (ageThisYear >= startAge) pension += p.pension.amount;
      }
    }

    const target = input.targetSpend * Math.pow(1 + cola, year - 1);
    const withdrawals = Math.max(0, target - ss - pension - wages);
    series.push({
      year,
      withdrawals: Math.round(withdrawals / 1000),
      socialSecurity: Math.round(ss / 1000),
      pension: Math.round(pension / 1000),
      wages: Math.round(wages / 1000),
    });
  }
  return series;
}

/**
 * REAL starting-plan income stack (schema v8) — maps SIPS's modeled per-year
 * income dollars to the chart's $K bars. No synthesis: real claiming ages,
 * COLAs, and account distributions from the SC01 scenario.
 */
export function buildIncomeSeriesFromReal(points: IncomeSeriesPoint[]): IncomeYear[] {
  return points.map((p) => ({
    year: p.year,
    withdrawals: Math.round(p.withdrawals / 1000),
    socialSecurity: Math.round(p.socialSecurity / 1000),
    pension: Math.round(p.pension / 1000),
    wages: Math.round(p.wages / 1000),
  }));
}

export interface DivergenceYear {
  year: number;
  starting: number;
  /** Absent for years where the optimized line isn't drawn yet (toggle). */
  optimized: number;
}

/**
 * Flat-rate vs bucketed growth divergence (Align deep dive). The starting
 * line compounds at a flat 5%; the optimized line is the starting line plus
 * an eased share of the driver's REAL wealthAdded, so the end-of-plan gap
 * equals the real number.
 */
export function buildDivergenceSeries(input: {
  totalSavings: number;
  planHorizonYears: number;
  wealthAdded: number;
}): DivergenceYear[] {
  const horizon = Math.max(2, Math.min(60, Math.round(input.planHorizonYears || 30)));
  const series: DivergenceYear[] = [];
  for (let year = 1; year <= horizon; year++) {
    const t = (year - 1) / (horizon - 1);
    const starting = input.totalSavings * Math.pow(1.05, year - 1);
    // easeIn so the divergence builds gradually (compounding-like shape)
    const optimized = starting + input.wealthAdded * t * t;
    series.push({
      year,
      starting: +(starting / 1_000_000).toFixed(2),
      optimized: +(optimized / 1_000_000).toFixed(2),
    });
  }
  return series;
}

/**
 * REAL divergence series (schema v8) — maps SIPS's per-year modeled balances
 * (dollars) to the chart's $M shape. No synthesis: both lines are plan data.
 */
export function buildDivergenceSeriesFromReal(points: AlignmentSeriesPoint[]): DivergenceYear[] {
  return points.map((p) => ({
    year: p.year,
    starting: +(p.baseline / 1_000_000).toFixed(2),
    optimized: +(p.optimized / 1_000_000).toFixed(2),
  }));
}

export interface AccountMixState {
  /** X-axis tick label — wording depends on what the two bars actually are. */
  state: string;
  taxFree: number; // $M
  taxDeferred: number;
  taxable: number;
}

/**
 * The two bars plus what they mean. `likeForLike` is true only when BOTH bars
 * are end-of-plan balances (schema v9); otherwise the "before" bar is today's
 * balances and the difference includes horizon growth, which the panel must
 * not attribute to the optimization.
 */
export interface AccountMixResult {
  data: AccountMixState[];
  likeForLike: boolean;
}

/**
 * Synthesized account mix for reports with no real end-of-plan data. "Today"
 * is the real current bucket split. "End of plan" shifts the (real) conversion
 * total from tax-deferred to tax-free and, when the optimized end-of-plan
 * assets are known, scales the whole bar so its total matches the real SC07
 * figure. NOT like for like — the second bar is decades later, so the gap is
 * mostly growth.
 */
export function buildAccountMix(input: {
  savings: { taxable: number; taxDeferred: number; taxFree: number };
  conversionsTotal: number;
  endOfPlanAssets?: number | null;
}): AccountMixResult {
  const m = (n: number) => +(n / 1_000_000).toFixed(2);
  const before: AccountMixState = {
    state: 'Today',
    taxFree: m(input.savings.taxFree),
    taxDeferred: m(input.savings.taxDeferred),
    taxable: m(input.savings.taxable),
  };

  const movedFromDeferred = Math.min(input.conversionsTotal, input.savings.taxDeferred);
  let afterFree = input.savings.taxFree + movedFromDeferred;
  let afterDeferred = input.savings.taxDeferred - movedFromDeferred;
  let afterTaxable = input.savings.taxable;

  // Scale the after-bar to the real optimized end-of-plan total when known,
  // so the chart's headline magnitude is true to the plan.
  const afterTotal = afterFree + afterDeferred + afterTaxable;
  if (input.endOfPlanAssets && input.endOfPlanAssets > 0 && afterTotal > 0) {
    const scale = input.endOfPlanAssets / afterTotal;
    afterFree *= scale;
    afterDeferred *= scale;
    afterTaxable *= scale;
  }

  return {
    data: [
      before,
      { state: 'End of plan', taxFree: m(afterFree), taxDeferred: m(afterDeferred), taxable: m(afterTaxable) },
    ],
    likeForLike: false,
  };
}

/**
 * REAL account mix from SIPS — no shift-and-scale.
 *
 * With `baselineMix` (schema v9) both bars are END-OF-PLAN balances: the
 * attribution baseline (SC04 -> SC03 -> SC01) against the proposed plan, so
 * the difference is the optimization rather than horizon growth.
 *
 * Without it (schema v8) only the proposed plan's end-of-plan mix is known,
 * so the first bar falls back to today's balances and the result is flagged
 * NOT like for like.
 */
export function buildAccountMixFromReal(input: {
  savings: { taxable: number; taxDeferred: number; taxFree: number };
  endMix: EndOfPlanAccountMix;
  baselineMix?: EndOfPlanAccountMix | null;
}): AccountMixResult {
  const m = (n: number) => +(n / 1_000_000).toFixed(2);
  const likeForLike = !!input.baselineMix;
  const before = input.baselineMix
    ? {
        state: 'As-is plan',
        taxFree: m(input.baselineMix.taxFree),
        taxDeferred: m(input.baselineMix.taxDeferred),
        taxable: m(input.baselineMix.taxable),
      }
    : {
        state: 'Today',
        taxFree: m(input.savings.taxFree),
        taxDeferred: m(input.savings.taxDeferred),
        taxable: m(input.savings.taxable),
      };
  return {
    data: [
      before,
      {
        state: likeForLike ? 'Optimized plan' : 'End of plan',
        taxFree: m(input.endMix.taxFree),
        taxDeferred: m(input.endMix.taxDeferred),
        taxable: m(input.endMix.taxable),
      },
    ],
    likeForLike,
  };
}

export interface RothTaxYear {
  year: number;
  cumWithout: number;
  cumWith: number;
  rateWithout: number;
  rateWith: number;
}

/**
 * REAL Roth tax series (schema v8): per-year cumulative taxes and effective
 * rates for SC06.01 vs its baseline, straight from SIPS. Break-even = the
 * first year the with-conversion cumulative drops back to/below the baseline
 * after having exceeded it (null when the lines never cross).
 */
export function buildRothTaxSeriesFromReal(
  points: TaxSeriesPoint[],
  planStartYear: number
): { series: RothTaxYear[]; breakEvenYear: number | null } {
  let breakEvenYear: number | null = null;
  let everAbove = false;
  const series = points.map((p) => {
    const calendarYear = planStartYear + p.year - 1;
    if (p.optimizedCumTax > p.baselineCumTax) {
      everAbove = true;
    } else if (everAbove && breakEvenYear === null) {
      breakEvenYear = calendarYear;
    }
    return {
      year: calendarYear,
      cumWithout: Math.round(p.baselineCumTax),
      cumWith: Math.round(p.optimizedCumTax),
      rateWithout: +p.baselineTaxRate.toFixed(1),
      rateWith: +p.optimizedTaxRate.toFixed(1),
    };
  });
  return { series, breakEvenYear };
}

/**
 * Roth tax-lines (effective rate + cumulative taxes, with/without the
 * conversion). Cumulative endpoints are pinned to the REAL SC01/SC07 lifetime
 * tax totals when available; the with-conversion line front-loads tax in the
 * (real) conversion years, then grows slower — the crossing is the projected
 * break-even.
 */
export function buildRothTaxSeries(input: {
  conversions: RothConversionEntry[];
  taxSavings: number;
  startingTotalTaxes?: number | null;
  optimizedTotalTaxes?: number | null;
  planHorizonYears: number;
  planStartYear: number;
}): { series: RothTaxYear[]; breakEvenYear: number | null } {
  const horizon = Math.max(4, Math.min(60, Math.round(input.planHorizonYears || 30)));
  const withoutEnd =
    input.startingTotalTaxes && input.startingTotalTaxes > 0
      ? input.startingTotalTaxes
      : Math.max(input.taxSavings * 3, 500_000);
  const withEnd =
    input.optimizedTotalTaxes && input.optimizedTotalTaxes > 0
      ? input.optimizedTotalTaxes
      : Math.max(withoutEnd - input.taxSavings, withoutEnd * 0.6);

  const conversionYears = new Set(input.conversions.filter((c) => c.amount > 0).map((c) => c.year));
  const conversionTotal = input.conversions.reduce((s, c) => s + Math.max(0, c.amount), 0);
  // Modeled conversion tax paid up front (24% bracket assumption, illustrative).
  const upFrontTax = Math.min(conversionTotal * 0.24, withEnd * 0.6);

  const series: RothTaxYear[] = [];
  let breakEvenYear: number | null = null;
  const lastConversionYear = Math.max(1, ...conversionYears);

  for (let year = 1; year <= horizon; year++) {
    const t = year / horizon;
    // Without: accelerating curve (RMDs push taxes up later) reaching withoutEnd.
    const cumWithout = withoutEnd * Math.pow(t, 1.35);
    // With: front-loaded conversion tax + slower tail reaching withEnd.
    const frontShare = Math.min(1, year / lastConversionYear);
    const cumWith = upFrontTax * frontShare + (withEnd - upFrontTax) * Math.pow(t, 1.8);

    // Effective-rate lines (illustrative shapes matching the story).
    const inConversion = conversionYears.has(year);
    const rateWithout = 17 + 6 * Math.pow(t, 1.6);
    const rateWith = inConversion ? 24 : 15 + 4 * Math.pow(t, 1.4);

    if (breakEvenYear === null && year > 1 && cumWith <= cumWithout) {
      breakEvenYear = input.planStartYear + year - 1;
    }

    series.push({
      year: input.planStartYear + year - 1,
      cumWithout: Math.round(cumWithout),
      cumWith: Math.round(cumWith),
      rateWithout: +rateWithout.toFixed(1),
      rateWith: +rateWith.toFixed(1),
    });
  }
  return { series, breakEvenYear };
}

/**
 * Approximate before/after Social Security benefits from claim ages.
 * Delayed credits ≈ +8%/yr past FRA; early claiming ≈ −6.25%/yr before FRA.
 */
export function buildSsBenefits(input: {
  fraAmount: number;
  fraAge?: number;
  claimAgeBefore: number;
  claimAgeAfter: number;
}): { before: number; after: number } {
  const fraAge = input.fraAge ?? 67;
  const factor = (claimAge: number) => {
    const delta = claimAge - fraAge;
    return delta >= 0 ? 1 + 0.08 * delta : 1 - 0.0625 * Math.abs(delta);
  };
  return {
    before: Math.round(input.fraAmount * factor(input.claimAgeBefore)),
    after: Math.round(input.fraAmount * factor(input.claimAgeAfter)),
  };
}
